import React, { useMemo, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { lmsAdmin } from '../api'
import { FAILED_STATE, LmsGrid, NameCell, PhoneLine, useGridRows } from '../lms-grid'
import { usePresetAccess } from './access'
import { LauncherVersions } from './launcher-versions'
import { Pill } from '../../../shared/sso-workspace/pill'
import { useGridList } from '../../../shared/sso-workspace/fleet-grid'
import { useIsMobile } from '../../../shared/hooks/use-is-mobile'
import { TableAgClient } from '../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnText } from '../../../shared/table-builder/table-columns'
import { IconAdd, IconList, IconShield } from '../../../shared/icon/icon'
import { MenuItem } from '../../../shared/menu/menu'
import '../lms-admin.scss'
import { PageTrail } from '../../../shared/sso-workspace/page-trail'
import { SECTION } from '../../../shared/navigation/sections'

// LMS Presets (docs/08 §7, design edxr-lms-presets-v2.0.html) —
// sys-admin-curated platform presets that connections are built from.
// The backend gates every /admin/lms/presets route behind sys claims.

// A preset's status pill: published is green, draft is info, archived grey.
// Every status the editor saves has its own entry (a test checks it), so a
// status the list does not know cannot pass for grey.
export const STATUS_TONE = {
  published: 'ok',
  draft: 'info',
  archived: null
}

// What the grid says when the server refused the list (the gate normally stops
// anyone without the scope before they get here).
const FORBIDDEN_STATE = { Icon: IconShield, title: "You don't have access to LMS presets." }

export const ViewPresets = () => {
  const history = useHistory()
  const { canEdit, reason } = usePresetAccess()

  // The grid's ready (and its footer's reload) is the loader (useGridRows).
  const { rows: presets, error, ready } = useGridRows(() => lmsAdmin.listPresets().then(result => result.presets || []))
  const tableRef = useRef()
  const mobile = useIsMobile()

  // The cells' words, flattened onto the row: the grid sorts, quick-filters
  // and exports the cell VALUE, so each column's field holds what it shows.
  const gridRows = useMemo(
    () =>
      Array.isArray(presets)
        ? presets.map(preset => ({
          ...preset,
          family: preset.lms_family || '—',
          scorm: preset.scorm_version || '',
          phone: mobile
        }))
        : presets,
    [presets, mobile]
  )
  const gridList = useGridList(gridRows)
  // The card's menu (owner 2026-10-29): New preset pinned, kept on a phone.
  const menu = <MenuItem key="new" Icon={IconAdd} text="New preset" pin keepOnMobile show={canEdit} onClick={() => history.push('/org/lms/presets/new')} />

  return (
    <div className="lms-admin" data-testid="lms-presets">
      {/* The sidebar's LMS presets, not the organization's LMS (audit F11). */}
      <PageTrail trail={[SECTION.lmsPresets]} />
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-header-title">LMS Presets</h1>
          <p className="page-header-sub">
            Platform presets every organization connection builds from — SCORM flavor, deep-link config, writeback quirks
            per LMS family. System scope: changes affect all organizations.
          </p>
        </div>
      </div>

      {!canEdit && reason && (
        <div className="alert alert-warning" role="note" data-testid="presets-read-only">
          {reason}
        </div>
      )}

      {error && (
        <div className="alert alert-danger" role="alert">
          {error.status === 403
            ? 'LMS presets are system-admin only.'
            : `Couldn't load presets: ${error.message}`}
        </div>
      )}

      <LmsGrid title="presets" tableRef={tableRef} exportName="lms-presets" menu={menu}>
        <TableAgClient
          ref={tableRef}
          apiList={gridList}
          onReady={ready}
          sortCol="name"
          rowHeight={ROW_HEIGHT_TWO_LINE}
          onRowClicked={preset => history.push(`/org/lms/presets/${preset.id}`)}
          emptyState={presets === null ? (error && error.status === 403 ? FORBIDDEN_STATE : FAILED_STATE) : {
            Icon: IconList,
            title: 'No presets yet',
            message: "Connections can't be created until at least one preset is published.",
            action: { label: 'New preset', Icon: IconAdd, onClick: () => history.push('/org/lms/presets/new'), show: canEdit }
          }}
          noMatchState={{ message: 'No presets match. Clear the search.' }}>
          <ColumnFormat field="name" header="Preset" render={presetCell} exportValue={exportPreset} stacked minWidth={mobile ? 150 : 240} flex={2} sortable />
          <ColumnText field="family" header="Family" sortable hide={mobile} />
          <ColumnText field="scorm" header="SCORM" sortable hide={mobile} />
          <ColumnFormat field="status" header="Status" render={statusCell} sortable hide={mobile} />
        </TableAgClient>
      </LmsGrid>

      <LauncherVersions />
    </div>
  )
}

// The Preset column's CSV value: the name and the slug the cell shows under it.
export const exportPreset = preset => `${preset.name} (${preset.slug})`

const statusCell = cell => (cell.data ? <Pill tone={STATUS_TONE[cell.data.status]}>{cell.data.status}</Pill> : '')

// The preset's monogram, name (a link to its editor) and slug; on a phone the
// status pill and the slug, in place of the columns a phone has no room for.
const presetCell = cell => {
  const preset = cell.data
  if (!preset) return ''
  return (
    <NameCell
      logoName={preset.name}
      name={preset.name}
      to={`/org/lms/presets/${preset.id}`}
      sub={preset.slug}
      phone={preset.phone ? <PhoneLine pill={<Pill tone={STATUS_TONE[preset.status]}>{preset.status}</Pill>}>{preset.slug}</PhoneLine> : null}
    />
  )
}
